/**
 * "Rediger ansatt" (owner brief and mockup, 2026-09-21): one form, saved with Lagre, closed with
 * Avbryt, and a question before unsaved changes are thrown away (owner, round 1). The fields are
 * the ones the record holds; emergency contact, notes and main workplace are left out until they
 * exist (owner, round 2). The role per company is edited under Tilgang, where it already works.
 *
 * PUT /admin/users/:id replaces the record, so the page's fullBody carries every field and this
 * form adds only what changed. The fødselsnummer is sent only when a new one is typed.
 */
import { ApiError } from '@/api/client';
import type { ApiTeam, ApiUserFull, UserWrite } from '@/api/data';
import { useAveraging } from '@/api/people';
import { useAuth } from '@/auth/store';
import { T } from '@/i18n';
import { today } from '@/rules';
import { setDirty, useDirty } from '@/shell/context';
import { useDiscard, useNeutralSave } from '@/shell/leave';
import { useTenant } from '@/shell/tenant';
import { Button, Drawer, Switch } from '@/ui';
import { DateInput, birthDay } from '@/ui/DateInput';
import { Icon } from '@/ui/icons';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { dropDraft, loadDraft, saveDraft } from './draft';
import { EmailTypo } from './emailTypo';

type Form = Record<string, string>;
const TABS = [
  { key: 'person', label: 'Person' },
  { key: 'arbeid', label: 'Arbeidstid og lønn' },
  { key: 'team', label: 'Team' },
] as const;

type DrawerTab = (typeof TABS)[number]['key'];
/** The part of the form a field is in, so an error or a setup link lands on the right one. */
const ARBEID = [
  'agreed_week_hours',
  'employment_start',
  'previous_employment_start',
  'previous_employment_end',
  'employment_form',
  'end_date',
  'hourly_rate',
  'monthly_rate',
  'rate_valid_from',
  'avspasering_agreed_on',
  'avspasering_share',
  'flex_agreed_on',
  'vacation_days_balance',
  'total_vacation_days_per_year',
  'averaging_start',
  'averaging_weeks',
  'averaging_target',
  'averaging_plan',
  'exempt_reason',
];
export const tabOfField = (k: string): DrawerTab => (ARBEID.includes(k) ? 'arbeid' : 'person');

const str = (v: unknown) => (v == null ? '' : String(v));
/** the fields whose change is a new row in the rate history, valid from «Gjelder fra» */
const PAY = ['contract_type', 'hourly_rate', 'monthly_rate'] as const;
const dec = (n: number) => String(n).replace('.', ',');

/**
 * AML §10-5 written plan (plan 0027 batch E, #112) as typed: one week per line or semicolon, and
 * "45 x 13" for thirteen weeks of 45 h; a comma is a decimal ("37,5"). Null when a part is not a
 * number of hours.
 */
export function parsePlan(text: string): number[] | null {
  const out: number[] = [];
  for (const part of text
    .split(/[;\n]+/)
    .map((x) => x.trim())
    .filter(Boolean)) {
    const m = /^(\d+(?:[.,]\d+)?)\s*(?:[x×*]\s*(\d+))?$/i.exec(part);
    if (!m) return null;
    const h = Number((m[1] as string).replace(',', '.'));
    const n = m[2] ? Number(m[2]) : 1;
    if (n < 1 || n > 52) return null;
    for (let i = 0; i < n; i++) out.push(h);
  }
  return out;
}
/** The stored plan as the field shows it: runs of the same week folded, "45 x 13; 35 x 13". */
export function planText(plan: number[] | null | undefined): string {
  const runs: [number, number][] = [];
  for (const h of plan ?? []) {
    const last = runs[runs.length - 1];
    if (last && last[0] === h) last[1]++;
    else runs.push([h, 1]);
  }
  return runs.map(([h, n]) => (n > 1 ? `${dec(h)} x ${n}` : dec(h))).join('; ');
}
/** The fields by the label the form shows them under: the conflict list names them so (#270). */
const LABEL: Record<string, string> = {
  name: 'Fornavn',
  lastname: 'Etternavn',
  username: 'Brukernavn',
  employee_id: 'Ansattnummer',
  payroll_id: 'Lønns-ID i denne bedriften',
  birth_date: 'Fødselsdato',
  email: 'E-post',
  phone: 'Telefon',
  address: 'Adresse',
  agreed_week_hours: 'Avtalt arbeidstid, timer per uke',
  employment_start: 'Ansatt fra',
  employment_form: 'Ansettelsesform',
  end_date: 'Sluttdato',
  contract_type: 'Lønnstype',
  hourly_rate: 'Timelønn, kr per time',
  monthly_rate: 'Månedslønn, kr',
  vacation_days_balance: 'Feriedager igjen',
  total_vacation_days_per_year: 'Feriedager per år',
};
/** The fields this form has changed from what it was read at, with the teams as one more. */
const changedKeys = (f: Form, from: Form) => Object.keys(f).filter((k) => (f[k] ?? '') !== (from[k] ?? ''));
const teamsOf = (u: ApiUserFull, here: Set<number>) =>
  (u.teams ?? []).map((t) => t.id).filter((id) => here.has(id));
/** A draft carries what it was read at, so it can be laid over a record that has moved since. */
interface Kept {
  f: Form;
  from: Form;
  tm: number[];
  tmFrom: number[];
  revision: string | null;
}
const cap = (v: string) => v.replace(/(^|[\s-])(\p{L})/gu, (_, a: string, b: string) => a + b.toUpperCase());

function initial(u: ApiUserFull): Form {
  return {
    name: cap(str(u.name)),
    lastname: cap(str(u.lastname)),
    username: str(u.username),
    employee_id: str(u.employee_id),
    payroll_id: str(u.payroll_id),
    personal_number: '',
    birth_date: str(u.birth_date).slice(0, 10),
    in_compulsory_school: u.in_compulsory_school == null ? '' : u.in_compulsory_school ? 'ja' : 'nei',
    children_under_12: str(u.children_under_12),
    sole_carer: u.sole_carer == null ? '' : u.sole_carer ? 'ja' : 'nei',
    chronically_ill_children: str(u.chronically_ill_children),
    email: str(u.email),
    phone: str(u.phone),
    address: str(u.address),
    agreed_week_hours: str(u.agreed_week_hours).replace('.', ','),
    employment_start: str(u.employment_start).slice(0, 10),
    previous_employment_start: str(u.previous_employment_start).slice(0, 10),
    previous_employment_end: str(u.previous_employment_end).slice(0, 10),
    employment_form: str(u.employment_form),
    end_date: str(u.end_date).slice(0, 10),
    exempt_position: str(u.exempt_position),
    averaging: str(u.averaging),
    averaging_start: str(u.averaging_start).slice(0, 10),
    averaging_weeks: str(u.averaging_weeks),
    averaging_target: u.averaging_target == null ? '' : dec(u.averaging_target),
    averaging_plan: planText(u.averaging_plan),
    exempt_reason: str(u.exempt_reason),
    avspasering_agreed_on: str(u.avspasering_agreed_on).slice(0, 10),
    avspasering_share: str(u.avspasering_share),
    flex_agreed_on: str(u.flex_agreed_on).slice(0, 10),
    contract_type: str(u.contract_type) || 'hourly',
    hourly_rate: str(u.hourly_rate),
    monthly_rate: str(u.monthly_rate),
    // «Gjelder fra» of a changed rate (rate history, 2026-11-04): empty is today, never read from the record
    rate_valid_from: '',
    vacation_days_balance: str(u.vacation_days_balance),
    total_vacation_days_per_year: str(u.total_vacation_days_per_year),
  };
}

/** Whole years on `today` from a YYYY-MM-DD birth date; null when there is none. */
export function ageOn(birth: string, today: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(birth)) return null;
  const [by, bm, bd] = birth.split('-').map(Number) as [number, number, number];
  const [y, m, d] = today.split('-').map(Number) as [number, number, number];
  return y - by - (m < bm || (m === bm && d < bd) ? 1 : 0);
}

/**
 * Whether the form asks about compulsory school: from 15 to 17, because the backend honours a yes at
 * every age under 18 (#245), so a yes typed at 15 must stay correctable at 16 and 17.
 */
export function asksSchool(birth: string, today: string): boolean {
  const age = ageOn(birth, today);
  return age != null && age >= 15 && age < 18;
}

/** What the form would change, as the fields the API takes; null when a field is not valid. */
export function changes(
  u: ApiUserFull,
  f: Form,
  teams: Set<number>,
  myTeams: number[],
): { body: UserWrite; errors: Record<string, string> } {
  const o = initial(u);
  const errors: Record<string, string> = {};
  const body: Record<string, unknown> = {};
  // the address may be empty (2026-09-29): the person is kept without one, "Ingen e-post ennå"
  for (const k of ['name', 'lastname', 'username'] as const) if (!f[k]?.trim()) errors[k] = T('Må fylles ut');
  if (f.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()))
    errors.email = T('Ikke en e-postadresse');
  const whole = (k: string, min: number) => {
    const v = f[k]?.trim() ?? '';
    if (v === '') return null;
    const n = Number(v.replace(',', '.'));
    if (!Number.isInteger(n)) {
      errors[k] = T('Hele tall');
      return undefined;
    }
    if (n < min) {
      errors[k] = T('Minst {n}', { n: min });
      return undefined;
    }
    return n;
  };
  const money = (k: string) => {
    const v = f[k]?.trim() ?? '';
    if (v === '') return null;
    const n = Number(v.replace(/\s/g, '').replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) {
      errors[k] = T('Et beløp i kroner');
      return undefined;
    }
    return String(n);
  };
  const text = [
    'name',
    'lastname',
    'username',
    'employee_id',
    'payroll_id',
    'email',
    'phone',
    'address',
  ] as const;
  for (const k of text) if ((f[k] ?? '').trim() !== (o[k] ?? '').trim()) body[k] = (f[k] ?? '').trim();
  // plan 0027 batch A: set or corrected, never cleared (planning waits for it)
  if ((f.birth_date ?? '') !== (o.birth_date ?? '')) {
    const v = (f.birth_date ?? '').trim();
    if (v === '') errors.birth_date = T('Fødselsdatoen kan ikke fjernes');
    else if (!/^\d{4}-\d{2}-\d{2}$/.test(v) || v > new Date().toISOString().slice(0, 10) || v < '1900-01-01')
      errors.birth_date = T('En gyldig dato, ikke i fremtiden');
    else body.birth_date = v;
  }
  // plan 0027 batch A: still skolepliktig, asked for a 15 year old; empty is not known
  if ((f.in_compulsory_school ?? '') !== o.in_compulsory_school)
    body.in_compulsory_school = f.in_compulsory_school ? f.in_compulsory_school === 'ja' : null;
  // plan 0027 batch A, decision 2: the omsorgsdager facts (ftrl § 9-6); empty is not known
  for (const k of ['children_under_12', 'chronically_ill_children'] as const)
    if ((f[k] ?? '') !== (o[k] ?? '')) {
      const n = whole(k, 0);
      if (n !== undefined && n !== null && n > 20) errors[k] = T('Høyst {n}', { n: 20 });
      else if (n !== undefined) body[k] = n;
    }
  if ((f.sole_carer ?? '') !== o.sole_carer) body.sole_carer = f.sole_carer ? f.sole_carer === 'ja' : null;
  if (f.personal_number?.trim()) {
    if (!/^\d{11}$/.test(f.personal_number.replace(/\s/g, ''))) errors.personal_number = T('11 siffer');
    else body.personal_number = f.personal_number.replace(/\s/g, '');
  }
  // the person's own week in this company, decimals with a comma; empty follows the company's
  // (plan 0027 B, AML §14-6(1) j; at most 40, AML §10-4)
  if ((f.agreed_week_hours ?? '') !== (o.agreed_week_hours ?? '')) {
    const v = (f.agreed_week_hours ?? '').trim();
    const n = Number(v.replace(',', '.'));
    if (v === '') body.agreed_week_hours = null;
    else if (!Number.isFinite(n) || n <= 0 || n > 40 || Math.round(n * 100) !== n * 100)
      errors.agreed_week_hours = T('Et tall over 0 og høyst 40, f.eks. 22,5');
    else body.agreed_week_hours = n;
  }
  for (const k of ['vacation_days_balance', 'total_vacation_days_per_year'] as const)
    if (f[k] !== o[k]) {
      const n = whole(k, 0);
      if (n !== undefined) body[k] = n;
    }
  if (f.contract_type !== o.contract_type) body.contract_type = f.contract_type;
  if ((f.employment_start ?? '') !== o.employment_start) {
    const v = f.employment_start?.trim() ?? '';
    if (v && !/^\d{4}-\d{2}-\d{2}$/.test(v)) errors.employment_start = T('En dato');
    else body.employment_start = v || null;
  }
  // ftrl § 8-18 (2), plan 0027 batch I: the earlier employment here, which counts toward the four
  // weeks when Ansatt fra is within two weeks of its end
  for (const k of ['previous_employment_start', 'previous_employment_end'] as const)
    if ((f[k] ?? '') !== o[k]) {
      const v = f[k]?.trim() ?? '';
      if (v && !/^\d{4}-\d{2}-\d{2}$/.test(v)) errors[k] = T('En dato');
      else body[k] = v || null;
    }
  if (
    f.previous_employment_start &&
    f.previous_employment_end &&
    f.previous_employment_start > f.previous_employment_end &&
    !errors.previous_employment_end
  )
    errors.previous_employment_end = T('Må være etter starten');
  // aml § 14-9 and § 14-6 (1) e (#108): the form, and the end date a temporary job must have,
  // judged on the form as it will be after the save
  if ((f.employment_form ?? '') !== o.employment_form)
    body.employment_form = (f.employment_form || null) as never;
  if ((f.end_date ?? '') !== o.end_date) {
    const v = f.end_date?.trim() ?? '';
    if (v && !/^\d{4}-\d{2}-\d{2}$/.test(v)) errors.end_date = T('En dato');
    else body.end_date = v || null;
  }
  if (f.employment_form === 'midlertidig' && !f.end_date?.trim() && !errors.end_date)
    errors.end_date = T('Må fylles ut for en midlertidig ansettelse');
  // AML §10-5 and §10-12: one of the law's own values, or empty for the ordinary rules
  if ((f.exempt_position ?? '') !== o.exempt_position)
    body.exempt_position = (f.exempt_position || null) as never;
  // AML §10-12 (plan 0027 batch E): a position set or kept exempt carries the company's reason;
  // sent with the position, and the server refuses a blank one as well
  const reason = (f.exempt_reason ?? '').trim();
  if (
    f.exempt_position &&
    (body.exempt_position !== undefined || reason !== (o.exempt_reason ?? '').trim())
  ) {
    if (!reason) errors.exempt_reason = T('Skriv hvorfor stillingen er unntatt');
    else body.exempt_reason = reason;
  }
  if ((f.averaging ?? '') !== o.averaging) body.averaging = (f.averaging || null) as never;
  // AML §10-5 period (#112): the four facts go together, all of them or none; ending the
  // agreement ends its period on the server
  const PERIOD = ['averaging_start', 'averaging_weeks', 'averaging_target', 'averaging_plan'] as const;
  if (f.averaging && PERIOD.some((k) => (f[k] ?? '').trim() !== (o[k] ?? '').trim())) {
    const v = Object.fromEntries(PERIOD.map((k) => [k, (f[k] ?? '').trim()])) as Record<
      (typeof PERIOD)[number],
      string
    >;
    if (PERIOD.every((k) => v[k] === ''))
      Object.assign(body, {
        averaging_start: null,
        averaging_weeks: null,
        averaging_target: null,
        averaging_plan: null,
      });
    else {
      const weeks = Number(v.averaging_weeks);
      const target = Number(v.averaging_target.replace(',', '.'));
      const plan = parsePlan(v.averaging_plan);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(v.averaging_start)) errors.averaging_start = T('En dato');
      if (!Number.isInteger(weeks) || weeks < 1 || weeks > 52)
        errors.averaging_weeks = T('Fra 1 til 52 uker');
      if (!Number.isFinite(target) || target <= 0 || target > 40)
        errors.averaging_target = T('Over 0 og høyst 40');
      if (!plan) errors.averaging_plan = T('Timer per uke, f.eks. 45 x 13; 35 x 13');
      else if (Number.isInteger(weeks) && plan.length !== weeks)
        errors.averaging_plan = T('Planen har {n} uker, perioden {w}', { n: plan.length, w: weeks });
      if (!PERIOD.some((k) => errors[k]))
        Object.assign(body, {
          averaging_start: v.averaging_start,
          averaging_weeks: weeks,
          averaging_target: target,
          averaging_plan: plan,
        });
    }
  }
  for (const k of ['hourly_rate', 'monthly_rate'] as const)
    if (f[k] !== o[k]) {
      const n = money(k);
      if (n !== undefined) body[k] = n;
    }
  // «Gjelder fra» goes with a change of pay only; empty is today, a later date is refused (rate history, 2026-11-04)
  const from = f.rate_valid_from?.trim() ?? '';
  if (from && PAY.some((k) => k in body)) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) errors.rate_valid_from = T('En dato');
    else if (from > today()) errors.rate_valid_from = T('Ikke etter i dag');
    else body.rate_valid_from = from;
  }
  // AML §10-6(12), plan 0027 G2: the date of the written avspasering agreement, and the share of
  // overtime it covers ("helt eller delvis"); an empty share is 100
  if ((f.avspasering_agreed_on ?? '') !== o.avspasering_agreed_on) {
    const v = f.avspasering_agreed_on?.trim() ?? '';
    if (v && !/^\d{4}-\d{2}-\d{2}$/.test(v)) errors.avspasering_agreed_on = T('En dato');
    else body.avspasering_agreed_on = v || null;
  }
  // plan 0027 G2 slice 4: the flexitime agreement's date (fastlønn only; the server refuses others)
  if ((f.flex_agreed_on ?? '') !== o.flex_agreed_on) {
    const v = f.flex_agreed_on?.trim() ?? '';
    if (v && !/^\d{4}-\d{2}-\d{2}$/.test(v)) errors.flex_agreed_on = T('En dato');
    else body.flex_agreed_on = v || null;
  }
  if ((f.avspasering_share ?? '') !== o.avspasering_share) {
    const n = whole('avspasering_share', 1);
    if (n !== undefined && n !== null && n > 100) errors.avspasering_share = T('Høyst {n}', { n: 100 });
    else if (n !== undefined) body.avspasering_share = n;
  }
  const was = [...myTeams].sort().join(',');
  const now = [...teams].sort().join(',');
  if (was !== now) body.team_ids = [...teams];
  return { body: body as UserWrite, errors };
}

export function EditDrawer({
  u,
  open,
  onClose,
  save,
  teams,
  seeLoenn,
  seeSens,
  roleText,
  base: path,
  startTab = 'person',
  focus,
  reload,
  onNext,
}: {
  u: ApiUserFull;
  open: boolean;
  onClose: () => void;
  /** resolves when saved, throws when refused: an ApiError as it came, anything else with the message to show */
  save: (body: UserWrite, base: ApiUserFull) => Promise<void>;
  /** the record as it is now, read again after a 409 revision_conflict (#270) */
  reload?: () => Promise<ApiUserFull>;
  /** «Lagre og neste» (#270 EMP-02): after a save, the next person in the queue; absent when none is left */
  onNext?: (() => void) | null;
  teams: ApiTeam[];
  seeLoenn: boolean;
  seeSens: boolean;
  roleText: string;
  base: string;
  /** the part of the form the button that opened it belongs to */
  startTab?: DrawerTab;
  /** a field to open on and focus: a setup gap's link (manager redesign, 2026-10-09) */
  focus?: string | undefined;
}) {
  // this company's teams only: fullBody sends only these, and another company's id is refused
  const here = new Set(teams.map((t) => t.id));
  // the company's week, which an empty field follows; 37,5 where the company has set none (plan 0027 B)
  const { tenant } = useTenant();
  const meId = useAuth((s) => s.user?.id);
  const companyWeek = String(
    tenant?.agreed_week_hours != null ? Number(tenant.agreed_week_hours) : 37.5,
  ).replace('.', ',');
  // the record the form is relative to: the person as read, or as read again after a conflict (#270)
  const [base, setBase] = useState<ApiUserFull>(u);
  const myTeams = teamsOf(base, here);
  // A draft kept from before (Behold utkast, a refresh, Back, #270): only the fields it changed are
  // laid over the record as it is now, so a field someone else saved meanwhile is not put back
  const [kept] = useState(() => loadDraft<Kept>(tenant?.id, meId, u.id));
  const [f, setF] = useState<Form>(() => {
    const now = initial(u);
    if (!kept) return now;
    for (const k of changedKeys(kept.form.f, kept.form.from)) now[k] = kept.form.f[k] ?? '';
    return now;
  });
  const [tm, setTm] = useState<Set<number>>(() => {
    const k = kept?.form;
    return new Set(k && k.tm.join(',') !== k.tmFrom.join(',') ? k.tm.filter((id) => here.has(id)) : myTeams);
  });
  const [draftNote, setDraftNote] = useState<string | null>(() =>
    kept
      ? T('Ulagret utkast fra kl. {t} er hentet. Det er ikke lagret ennå.', {
          t: new Date(kept.at).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' }),
        }) +
        (kept.form.revision && u.revision && kept.form.revision !== u.revision
          ? ` ${T('Personen er endret siden utkastet. Feltene du ikke endret viser det som er lagret nå.')}`
          : '')
      : null,
  );
  const [tab, setTab] = useState<DrawerTab>(focus ? tabOfField(focus) : startTab);
  // after the dialog has placed its own focus, move it to the field the link named
  useEffect(() => {
    if (!focus || !open) return;
    const t = setTimeout(
      () => document.querySelector<HTMLElement>(`.drawer [name="${focus}"]`)?.focus({ preventScroll: true }),
      50,
    );
    return () => clearTimeout(t);
  }, [focus, open]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  // a save refused because someone else saved first (409 revision_conflict): the record as it is now
  const [conflict, setConflict] = useState<ApiUserFull | null>(null);
  const conflictRef = useRef<HTMLDivElement>(null);
  const { body, errors } = changes(base, f, tm, myTeams);
  const dirty = Object.keys(body).length > 0 || !!f.personal_number;
  useDirty('edit-employee', open && dirty, T('Rediger ansatt'));
  // every change is kept for the tab until it is saved or discarded; reverting to the record forgets it.
  // Once saved or discarded the drawer is on its way out, and its last renders must not write it back
  const finished = useRef(false);
  useEffect(() => {
    if (!open || finished.current) return;
    if (dirty)
      saveDraft(tenant?.id, meId, u.id, {
        f,
        from: initial(base),
        tm: [...tm].sort(),
        tmFrom: [...myTeams].sort(),
        revision: base.revision ?? null,
      } satisfies Kept);
    else dropDraft(tenant?.id, meId, u.id);
  });
  const forget = () => dropDraft(tenant?.id, meId, u.id);
  useDiscard('edit-employee', forget);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setF({ ...f, [k]: e.target.value });
    setMsg(null);
  };
  /** saved or discarded: the draft goes, the drawer closes, and nothing is left to ask about on leaving */
  const close = () => {
    finished.current = true;
    forget();
    setDirty('edit-employee', null);
    setAsking(false);
    onClose();
  };
  /** «Behold utkast»: the drawer closes and the draft waits for the next time it opens */
  const keep = () => {
    finished.current = true;
    setDirty('edit-employee', null);
    setAsking(false);
    onClose();
  };
  /** true when saved (or nothing to save); false keeps the drawer with the field's message showing */
  const submit = async (next = false): Promise<boolean> => {
    setTried(true);
    setAsking(false);
    // a conflict is answered first: Save or Enter again brings the choice back into view, never hides it
    if (conflict) {
      conflictRef.current?.focus();
      setMsg(T('Velg først hva du vil gjøre med endringene dine.'));
      return false;
    }
    const bad = Object.keys(errors);
    if (bad.length) {
      setTab(tabOfField(bad[0] as string));
      return false;
    }
    if (!dirty) {
      close();
      if (next) onNext?.();
      return true;
    }
    setBusy(true);
    setMsg(null);
    try {
      await save({ ...body, ...(base.revision ? { if_revision: base.revision } : {}) }, base);
      close();
      if (next) onNext?.();
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.code === 'revision_conflict' && reload) {
        try {
          setConflict(await reload());
          setTimeout(() => conflictRef.current?.focus(), 0);
        } catch {
          setMsg(T('Noen andre har endret personen. Last siden på nytt før du lagrer.'));
        }
      } else {
        setMsg((e as Error).message);
        // a refusal that names a field (a taken or ill-formed number, #294 ONB-07): that field's tab and
        // the field itself; what was typed stays in the form
        const field = (e as { field?: unknown }).field;
        if (typeof field === 'string') {
          setTab(tabOfField(field));
          setTimeout(() => document.getElementById(`ed-${field}`)?.focus(), 0);
        }
      }
      return false;
    } finally {
      setBusy(false);
    }
  };
  /** the conflict's two answers: take what is saved now, or lay this form's changes over it */
  const takeCurrent = () => {
    if (!conflict) return;
    setBase(conflict);
    setF(initial(conflict));
    setTm(new Set(teamsOf(conflict, here)));
    setConflict(null);
    setMsg(null);
    setDraftNote(T('Skjemaet viser det som er lagret nå. Endringene dine er forkastet.'));
  };
  const keepMine = () => {
    if (!conflict) return;
    const mine = changedKeys(f, initial(base));
    const now = initial(conflict);
    for (const k of mine) now[k] = f[k] ?? '';
    const teamsChanged = [...tm].sort().join(',') !== [...myTeams].sort().join(',');
    setBase(conflict);
    setF(now);
    if (!teamsChanged) setTm(new Set(teamsOf(conflict, here)));
    setConflict(null);
    setMsg(null);
    setDraftNote(
      T('Endringene dine er lagt over det som er lagret nå. Se over og lagre for å bekrefte dem.'),
    );
  };
  // a neutral edit: leaving the page can save it ("Lagre og fortsett", shell/leave.tsx)
  useNeutralSave('edit-employee', open && dirty ? () => submit() : null);
  // closing with changes asks in the drawer: save, keep the draft, or discard (#270; continue editing
  // stays one link away, and Escape on the question keeps editing)
  const [asking, setAsking] = useState(false);
  const attemptClose = () => {
    if (busy) return true;
    if (asking) {
      setAsking(false);
      return true;
    }
    if (!dirty) {
      forget();
      return false;
    }
    setAsking(true);
    return true;
  };
  const err = (k: string) => (tried && errors[k] ? <small className="ferr">{errors[k]}</small> : null);
  const input = (
    k: string,
    label: string,
    opts: {
      type?: string;
      req?: boolean;
      ph?: string;
      mode?: 'numeric' | 'decimal';
      after?: React.ReactNode;
    } = {},
  ) => (
    <label htmlFor={`ed-${k}`} className={`fld2${tried && errors[k] ? ' bad' : ''}`}>
      <span>
        {label}
        {opts.req && <span className="req"> *</span>}
      </span>
      {opts.type === 'date' ? (
        // the app's date field, not the browser's: its calendar button has the house focus ring (#272 B-V03)
        <DateInput
          // a birth date is in the past and opens decades back (#272 B-X02)
          {...(k === 'birth_date' ? birthDay() : {})}
          id={`ed-${k}`}
          className="inp"
          name={k}
          value={f[k] ?? ''}
          onValueChange={(v) => {
            setF({ ...f, [k]: v });
            setMsg(null);
          }}
          aria-label={label}
          aria-invalid={(tried && !!errors[k]) || undefined}
          required={opts.req}
        />
      ) : (
        <input
          id={`ed-${k}`}
          className="inp"
          name={k}
          type={opts.type ?? 'text'}
          value={f[k] ?? ''}
          onChange={set(k)}
          placeholder={opts.ph}
          inputMode={opts.mode}
          aria-invalid={tried && !!errors[k]}
          required={opts.req}
        />
      )}
      {err(k)}
      {opts.after}
    </label>
  );

  return (
    <Drawer
      wide
      open={open}
      onClose={close}
      blockClose={attemptClose}
      title={T('Rediger ansatt')}
      sub={T('Endringer lagres når du trykker Lagre.')}
      footer={
        asking ? (
          <>
            <span role="alert" className="ferr ed-ask">
              {T('Endringene er ikke lagret.')}{' '}
              <button type="button" className="lnk" onClick={() => setAsking(false)}>
                {T('Fortsett å redigere')}
              </button>
            </span>
            <span className="sp" />
            <Button ghost onClick={close} disabled={busy}>
              {T('Forkast')}
            </Button>
            <Button onClick={keep} autoFocus data-testid="ed-keep">
              {T('Behold utkast')}
            </Button>
            <Button tone="pri" onClick={() => submit()} disabled={busy}>
              {T('Lagre og lukk')}
            </Button>
          </>
        ) : (
          <>
            {msg && (
              <span className="ferr" role="alert">
                {msg}
              </span>
            )}
            <span className="sp" />
            <Button onClick={() => (attemptClose() ? null : close())} disabled={busy}>
              {T('Avbryt')}
            </Button>
            {onNext && (
              <Button onClick={() => submit(true)} disabled={busy} data-testid="ed-save-next">
                {T('Lagre og neste')}
              </Button>
            )}
            <Button tone="pri" onClick={() => submit()} disabled={busy}>
              {busy ? T('Lagrer…') : T('Lagre endringer')}
            </Button>
          </>
        )
      }
    >
      {conflict && (
        <div
          className="al warn ed-conflict"
          role="alert"
          tabIndex={-1}
          ref={conflictRef}
          data-testid="ed-conflict"
        >
          <Icon name="warn" />
          <div>
            <b>{T('Opplysningene ble endret mens du redigerte. Ingenting av ditt er lagret.')}</b>
            <ul>
              {changedKeys(f, initial(base)).map((k) => {
                const now = initial(conflict)[k] ?? '';
                return (
                  <li key={k}>
                    {T(LABEL[k] ?? k)}:{' '}
                    {T('lagret nå {a}, ditt forslag {b}', { a: now || T('tomt'), b: f[k] || T('tomt') })}
                  </li>
                );
              })}
            </ul>
            <div className="ed-cbtns">
              <Button onClick={takeCurrent}>{T('Bruk det som er lagret nå')}</Button>
              <Button tone="pri" onClick={keepMine} data-testid="ed-keep-mine">
                {T('Behold mine endringer')}
              </Button>
            </div>
          </div>
        </div>
      )}
      {draftNote && !conflict && (
        <output className="al info ed-draft" data-testid="ed-draft">
          <Icon name="info" />
          <span>{draftNote}</span>
          {kept && dirty && (
            <button
              type="button"
              className="lnk"
              onClick={() => {
                setF(initial(base));
                setTm(new Set(myTeams));
                forget();
                setDraftNote(null);
              }}
            >
              {T('Forkast utkastet')}
            </button>
          )}
        </output>
      )}
      <div className="tabs dtabs" role="tablist">
        {TABS.map((x) => (
          <button
            key={x.key}
            type="button"
            role="tab"
            aria-selected={tab === x.key}
            className={tab === x.key ? 'on' : ''}
            onClick={() => setTab(x.key)}
          >
            {T(x.label)}
          </button>
        ))}
      </div>
      <form
        className="ed-form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {tab === 'person' && (
          <>
            <h4 className="fsec">{T('Personinformasjon')}</h4>
            <div className="fgrid">
              {input('name', T('Fornavn'), { req: true })}
              {input('lastname', T('Etternavn'), { req: true })}
              {input('employee_id', T('Ansattnummer'), { ph: T('F.eks. 1001') })}
              {input('payroll_id', T('Lønns-ID i denne bedriften'), {
                ph: T('Nummer i lønnssystemet'),
              })}
              {input('birth_date', T('Fødselsdato'), { type: 'date' })}
              {/* aml §§ 11-2 (2), 11-3 (1), 11-5 (2) a: a pupil still in compulsory school has a
                  child's limits at any age under 18 (plan 0027 batch A, #245); unknown is the child's
                  limits at 15 and the 15 to 17 limits from 16 */}
              {asksSchool(f.birth_date ?? '', new Date().toISOString().slice(0, 10)) && (
                <div className="fld">
                  <span className="lbl">{T('Skolepliktig (går i grunnskolen)')}</span>
                  <select
                    className="inp w"
                    aria-label={T('Skolepliktig (går i grunnskolen)')}
                    value={f.in_compulsory_school ?? ''}
                    onChange={(e) => setF({ ...f, in_compulsory_school: e.target.value })}
                  >
                    <option value="">
                      {ageOn(f.birth_date ?? '', new Date().toISOString().slice(0, 10)) === 15
                        ? T('Ukjent, strengeste regler brukes')
                        : T('Ukjent')}
                    </option>
                    <option value="ja">{T('Ja')}</option>
                    <option value="nei">{T('Nei, ferdig med grunnskolen')}</option>
                  </select>
                </div>
              )}
              {seeSens ? (
                input('personal_number', T('Fødselsnummer'), {
                  mode: 'numeric',
                  ph: u.personal_number ? `•••••• ${String(u.personal_number).slice(-5)}` : T('11 siffer'),
                })
              ) : (
                <span />
              )}
              {input('email', T('E-post'), {
                type: 'email',
                ph: T('Ingen e-post ennå'),
                after: <EmailTypo value={f.email ?? ''} onFix={(email) => setF({ ...f, email })} />,
              })}
              {input('phone', T('Telefon'), { type: 'tel' })}
            </div>
            <div className="fgrid one">{input('address', T('Adresse'))}</div>
            {/* plan 0027 batch A: the facts the sykt barn quota reads (ftrl § 9-6), never a diagnosis */}
            <h4 className="fsec">{T('Barn (omsorgsdager)')}</h4>
            <div className="fgrid">
              {input('children_under_12', T('Barn til og med 12 år'), { mode: 'numeric', ph: T('Ukjent') })}
              {input('chronically_ill_children', T('Barn med utvidet rett fra Nav'), {
                mode: 'numeric',
                ph: T('Ukjent'),
              })}
              <div className="fld">
                <span className="lbl">{T('Alene om omsorgen')}</span>
                <select
                  className="inp w"
                  aria-label={T('Alene om omsorgen')}
                  value={f.sole_carer ?? ''}
                  onChange={(e) => setF({ ...f, sole_carer: e.target.value })}
                >
                  <option value="">{T('Ukjent')}</option>
                  <option value="ja">{T('Ja')}</option>
                  <option value="nei">{T('Nei')}</option>
                </select>
              </div>
            </div>
            <p className="hint">
              {T(
                'Brukes bare til kvoten for sykt barn: 10 dager, 15 med tre eller flere barn, dobbelt for den som er alene, og 10 til for hvert barn med utvidet rett (folketrygdloven § 9-6). Uten antall barn varsler Timefly fra 10 dager.',
              )}
            </p>
            <h4 className="fsec">{T('Innlogging')}</h4>
            <div className="fgrid">{input('username', T('Brukernavn'), { req: true })}</div>
            {seeSens && (
              <p className="hint">
                {T('Fødselsnummeret vises aldri i klartekst. Skriv et nytt bare for å endre det.')}
              </p>
            )}
          </>
        )}
        {tab === 'arbeid' && (
          <>
            <h4 className="fsec">{T('Arbeidstid')}</h4>
            <div className="fgrid">
              {input('agreed_week_hours', T('Avtalt arbeidstid, timer per uke'), {
                mode: 'decimal',
                ph: T('følger bedriften ({h} t)', { h: companyWeek }),
              })}
            </div>
            <p className="hint">
              {T(
                'Tomt: følger bedriftens uke, {h} t (Innstillinger → Arbeidstid). Deltid: skriv timene, f.eks. 22,5.',
                {
                  h: companyWeek,
                },
              )}
            </p>
            <div className="fgrid">{input('employment_start', T('Ansatt fra'), { type: 'date' })}</div>
            <p className="hint">
              {T(
                'Arbeidsgiver betaler sykepenger først etter fire ukers ansettelse; før det betaler Nav fra første dag. Egenmelding kan brukes etter to måneder.',
              )}
            </p>
            <div className="fgrid">
              {input('previous_employment_start', T('Forrige ansettelse her, fra'), { type: 'date' })}
              {input('previous_employment_end', T('Forrige ansettelse her, til'), { type: 'date' })}
            </div>
            <p className="hint">
              {T(
                'Begynte personen igjen innen to uker etter at en tidligere ansettelse her sluttet, teller den med i de fire ukene (folketrygdloven § 8-18). Tomt hvis ikke.',
              )}
            </p>
            <div className="fgrid">
              <div className={`fld2${tried && errors.employment_form ? ' bad' : ''}`}>
                <span>{T('Ansettelsesform')}</span>
                <select
                  className="inp"
                  name="employment_form"
                  aria-label={T('Ansettelsesform')}
                  value={f.employment_form}
                  onChange={(e) => setF({ ...f, employment_form: e.target.value })}
                >
                  <option value="">{T('Ikke oppgitt')}</option>
                  <option value="fast">{T('Fast')}</option>
                  <option value="midlertidig">{T('Midlertidig')}</option>
                  <option value="tilkalling">{T('Tilkalling')}</option>
                </select>
              </div>
              {input('end_date', T('Sluttdato'), { type: 'date', req: f.employment_form === 'midlertidig' })}
            </div>
            <p className="hint">
              {T(
                'Arbeidsavtalen skal si om ansettelsen er fast, midlertidig eller tilkalling (arbeidsmiljøloven § 14-9), og en midlertidig ansettelse skal ha en sluttdato (§ 14-6). Timefly varsler 30 dager før sluttdatoen og når den er passert.',
              )}
            </p>
            <div className="fld">
              <span className="lbl">{T('Gjennomsnittsberegning')}</span>
              <select
                className="inp w"
                aria-label={T('Gjennomsnittsberegning')}
                value={f.averaging}
                onChange={(e) => setF({ ...f, averaging: e.target.value })}
              >
                <option value="">{T('Ingen avtale')}</option>
                <option value="individual">{T('Skriftlig avtale med den ansatte')}</option>
                {/* AML §10-5(2): for a tariff-bound company only (Innstillinger → Bedrift, batch C);
                    one already saved is still shown */}
                {(tenant?.tariff_bound || u.averaging === 'tillitsvalgte') && (
                  <option value="tillitsvalgte">{T('Avtale med tillitsvalgte')}</option>
                )}
              </select>
            </div>
            <p className="hint">
              {T(
                'Med avtale er alminnelig arbeidstid inntil 10 timer i døgnet og 48 i uka, 12,5 timer med tillitsvalgte (arbeidsmiljøloven § 10-5). Uten avtale gjelder 9 og 40.',
              )}
            </p>
            {f.averaging && (
              <>
                <div className="fgrid">
                  {input('averaging_start', T('Perioden starter'), { type: 'date' })}
                  {input('averaging_weeks', T('Antall uker (høyst 52)'), { mode: 'numeric' })}
                  {input('averaging_target', T('Snitt per uke, timer'), { mode: 'decimal', ph: '40' })}
                </div>
                <label className={`fld2${tried && errors.averaging_plan ? ' bad' : ''}`}>
                  <span>{T('Arbeidsplan, timer per uke')}</span>
                  <textarea
                    className="inp"
                    name="averaging_plan"
                    rows={2}
                    value={f.averaging_plan ?? ''}
                    placeholder="45 x 13; 35 x 13"
                    onChange={(e) => setF({ ...f, averaging_plan: e.target.value })}
                  />
                  {err('averaging_plan')}
                </label>
                <p className="hint">
                  {T(
                    'Avtalen gjelder bare med en skriftlig plan for hele perioden. Skriv timene uke for uke, «45 x 13; 35 x 13» er 13 uker på 45 og 13 på 35. Vaktplanen erstatter planen etter hvert som vakter legges inn.',
                  )}
                </p>
                <AveragingStatusLine u={u} />
              </>
            )}
            <div className="fld">
              <span className="lbl">{T('Unntatt arbeidstidsreglene')}</span>
              <select
                className="inp w"
                aria-label={T('Unntatt arbeidstidsreglene')}
                value={f.exempt_position}
                onChange={(e) => setF({ ...f, exempt_position: e.target.value })}
              >
                <option value="">{T('Nei, vanlige regler')}</option>
                <option value="leading">{T('Ledende stilling')}</option>
                <option value="independent">{T('Særlig uavhengig stilling')}</option>
              </select>
            </div>
            <p className="hint">
              {T(
                'Ledende og særlig uavhengige stillinger er unntatt arbeidstidskapittelet, så Timefly varsler ikke om dager, uker, pauser eller hvile for dem (arbeidsmiljøloven § 10-12). Kravet er strengt: stillingen må virkelig styre sin egen arbeidstid.',
              )}
            </p>
            {f.exempt_position && (
              <label className={`fld2${tried && errors.exempt_reason ? ' bad' : ''}`}>
                <span>
                  {T('Begrunnelse')}
                  <span className="req"> *</span>
                </span>
                <textarea
                  className="inp"
                  name="exempt_reason"
                  rows={2}
                  maxLength={1000}
                  value={f.exempt_reason ?? ''}
                  placeholder={T('Hvorfor styrer stillingen sin egen arbeidstid?')}
                  onChange={(e) => setF({ ...f, exempt_reason: e.target.value })}
                />
                {err('exempt_reason')}
                {u.exempt_position && u.exempt_set_at && (
                  <small className="hint" data-testid="exempt-audit">
                    {T('Satt av {who} {when}', {
                      who: u.exempt_set_by_name ?? T('en tidligere bruker'),
                      when: new Date(u.exempt_set_at).toLocaleDateString('nb-NO'),
                    })}
                  </small>
                )}
              </label>
            )}
            {seeLoenn && (
              <>
                <h4 className="fsec">{T('Lønn')}</h4>
                <div className="seg" style={{ marginBottom: 12 }}>
                  <button
                    type="button"
                    aria-pressed={f.contract_type !== 'monthly'}
                    onClick={() => setF({ ...f, contract_type: 'hourly' })}
                  >
                    {T('Timelønn')}
                  </button>
                  <button
                    type="button"
                    aria-pressed={f.contract_type === 'monthly'}
                    onClick={() => setF({ ...f, contract_type: 'monthly' })}
                  >
                    {T('Fastlønn')}
                  </button>
                </div>
                <div className="fgrid">
                  {f.contract_type === 'monthly'
                    ? input('monthly_rate', T('Månedslønn, kr'), { mode: 'decimal' })
                    : input('hourly_rate', T('Timelønn, kr per time'), { mode: 'decimal' })}
                  {PAY.some((k) => (f[k] ?? '') !== (initial(base)[k] ?? '')) &&
                    input('rate_valid_from', T('Gjelder fra'), { type: 'date' })}
                </div>
                {PAY.some((k) => (f[k] ?? '') !== (initial(base)[k] ?? '')) && (
                  <p className="hint" data-testid="rate-from-hint">
                    {T(
                      'Tom: fra i dag. Timer før datoen beholder satsen de hadde. En dato bakover må ligge i en lønnsmåned som ikke er lukket eller eksportert.',
                    )}
                  </p>
                )}
                <h4 className="fsec">{T('Avspasering')}</h4>
                <div className="fgrid">
                  {input('avspasering_agreed_on', T('Skriftlig avtale om avspasering fra'), { type: 'date' })}
                  {input('avspasering_share', T('Andel av overtiden, prosent'), {
                    mode: 'numeric',
                    ph: '100',
                  })}
                </div>
                <p className="hint">
                  {T(
                    'Med skriftlig avtale går overtidstimene, eller den avtalte andelen, i timebanken og tas ut som fri senere. Overtidstillegget på minst 40 % betales uansett (arbeidsmiljøloven § 10-6). Tom dato: overtiden betales ut som før.',
                  )}
                </p>
                {f.contract_type === 'monthly' && (
                  <>
                    <div className="fgrid">
                      {input('flex_agreed_on', T('Avtale om fleksitid fra'), { type: 'date' })}
                    </div>
                    <p className="hint">
                      {T(
                        'Fleksitid for fastlønnede: timer jobbet over den avtalte uka går i timebanken, og timer under trekkes, uke for uke. Ferie, sykdom og helligdager senker det som forventes. Timer over overtidsgrensen er overtid.',
                      )}
                    </p>
                  </>
                )}
              </>
            )}
            <h4 className="fsec">{T('Ferie')}</h4>
            <div className="fgrid">
              {input('vacation_days_balance', T('Feriedager igjen'), { mode: 'numeric' })}
              {input('total_vacation_days_per_year', T('Feriedager per år'), { mode: 'numeric' })}
            </div>
          </>
        )}
        {tab === 'team' && (
          <>
            <h4 className="fsec">{T('Team i denne bedriften')}</h4>
            {teams.length ? (
              <div className="fteams">
                {teams.map((t) => (
                  <Switch
                    key={t.id}
                    checked={tm.has(t.id)}
                    onChange={() => {
                      const n = new Set(tm);
                      n.has(t.id) ? n.delete(t.id) : n.add(t.id);
                      setTm(n);
                    }}
                    label={t.name}
                  />
                ))}
              </div>
            ) : (
              <p className="hint">{T('Bedriften har ingen team.')}</p>
            )}
            <h4 className="fsec">{T('Rolle')}</h4>
            <p className="hint">
              {roleText}. {T('Rollen gjelder per bedrift og endres under')}{' '}
              <Link to={`${path}/tilgang`} onClick={close}>
                {T('Tilgang')}
              </Link>
              .
            </p>
          </>
        )}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Drawer>
  );
}

/**
 * Where the §10-5 period stands (#112): the average week with the shifts so far and the plan for
 * the rest, against the agreed average. "Periode mangler" for an agreement saved without one.
 */
function AveragingStatusLine({ u }: { u: ApiUserFull }) {
  const q = useAveraging(u.id, !!u.averaging);
  if (!u.averaging) return null;
  if (!u.averaging_start)
    return (
      <div className="al warn" data-testid="averaging-status">
        <span>!</span>
        <span>
          {T('Periode mangler: uten periode og plan dømmes bare døgnet og uka, ikke snittet over perioden.')}
        </span>
      </div>
    );
  const a = q.data;
  if (!a?.period || a.average == null || a.over == null) return null;
  return (
    <p className="hint" data-testid="averaging-status">
      {T('I snitt {avg} t per uke i perioden med vaktene så langt og planen for resten, avtalt {target} t.', {
        avg: dec(a.average),
        target: dec(a.period.target),
      })}{' '}
      {a.over > 0
        ? T('{h} t over snittet blir overtid ved periodens slutt.', { h: dec(a.over) })
        : T('Innenfor snittet.')}
    </p>
  );
}
